import { Component, OnInit, ViewChild, Input, ElementRef, Output, EventEmitter } from '@angular/core';
import {  uniq, uniqWith } from 'lodash';
import { BehaviorSubject, switchMap, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Address, isAddress } from 'src/app/models/Address';
import { BeachSite, isBeachSite } from 'src/app/models/BeachSite';
import { isPOI, POI } from '../../models/POI';

import { areaHasActiveSites, getBeachSitesInArea, isRegion, Region } from 'src/app/models/SiteGroups';
import { SearchTypes } from 'src/app/models/Search/SearchTypes';

import { isSuburb, Suburb } from 'src/app/models/Suburb';
import { SitesService } from 'src/app/services/sites.service';
import { ZoomToSearchResultService } from '../../beach-watch-map/ZoomToSearchResultService';
import { ListService } from 'src/app/services/list.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserFeedbackModalDialogComponent } from 'src/app/shared/custom-modal-dialog/user-feedback-modal-dialog/user-feedback-modal-dialog.component';
import { AppMonitoringService } from 'src/app/services/logging.service';

type POIorAddress = POI | Address;
type SearchGroupType = { [key : string] : (Address | BeachSite | POI | Region | Suburb)[]};

class SearchGroup {
	constructor(public name: string,
		public options : any[],  
		public searchType: SearchTypes) 
	{

	}
}

@Component({
	selector: 'app-search',
	templateUrl: './search.component.html',
	styleUrls: ['./search.component.scss'],
	providers: [SitesService, ListService]
})


export class SearchComponent implements OnInit {
	
	@ViewChild('searchInput', { read : ElementRef }) searchInputElementRef: ElementRef;
	@ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
	searchInputText : string | { [key : string] : any } | null | undefined = "";
	searchOptionGroups: BehaviorSubject<SearchGroup[]> = new BehaviorSubject<SearchGroup[]>([]);
	searchOptions : BehaviorSubject<SearchGroupType>;
	lookupSearchSub: Subscription;	
	regionsSubscription : Subscription;
	sitesSubscription : Subscription;
	selectedOption : any;	

	searchFormControl = new FormControl("");
	private searchSub : Subscription;

	private PROP_ID_FLD = "propId";
	private POI_ID_FLD = "poiId";
	private POI_NAME = "poiName";
	private ADDRESS = "address";
	private regions : BehaviorSubject<Region[]> = new BehaviorSubject<Region[]>([]);
	private sites$ : BehaviorSubject<BeachSite[]> = new BehaviorSubject<BeachSite[]>([]);	


	@Input()
	get allBeachSites() {
		return this.sites$.getValue();
	}

	set allBeachSites(value) {
		this.sites$.next(value);
	}

	constructor(private sitesService : SitesService,
		private listService : ListService,
		private zoomToSearchService : ZoomToSearchResultService,
		private userfeedbackModalDialog : UserFeedbackModalDialogComponent,private appMonitoringService: AppMonitoringService) 
	{
		this.searchOptions = new BehaviorSubject<SearchGroupType>(
			{
				[SearchTypes.Address] : [],
				[SearchTypes.POI] : [],
				[SearchTypes.Region] : [],
				[SearchTypes.Site] : [],
				[SearchTypes.Suburb] : []
			});
	}

	ngOnInit(): void {
		this.getAllSites();
		this.getAllRegions();
		
		this.searchSub = this.searchFormControl.valueChanges?.subscribe({
			next : (value) => {
				this.searchInputText = value;
				this.getMatchingItems();				
			},
			error : (ex : Error) => {

			},
			complete : () => {

			}
		});	
		
	}

	private getAllRegions() {
		this.regionsSubscription = this.listService.getRegionList().subscribe({
			next : (regions) => {				
				
				if(Array.isArray(regions))	
				{		
							
					this.regions.next(regions);	
					const optionsData = this.searchOptions.getValue();
					optionsData[SearchTypes.Region] = [...regions];							
					this.searchOptions.next(optionsData);	
				}
			},
			error : (error) => {
				
			},
			complete : () => {				
			}
		});	
	}

	private getAllSites() {
		this.sitesSubscription = this.sitesService.getSites().subscribe({
			next : (data) => {				
				this.sites$.next(data);
				const optionsData = this.searchOptions.getValue();
				optionsData[SearchTypes.Site] = [...data];						
				this.searchOptions.next(optionsData);
			},
			error : (error) => {		
			},
			complete : () => {
				
			}
		});		
	}

	ngOnDestroy(): void {
		this.lookupSearchSub?.unsubscribe();
		this.regionsSubscription?.unsubscribe();		
	}	

	private getMatchingItems = () => {
		const lookupText = this.searchInputElementRef.nativeElement?.value ?? "";
		if("" === lookupText.trim())
		{
			this.searchOptionGroups.next([]);
			return;
		}
		const searchResults = this.sitesService.lookupSearchText(lookupText).pipe(switchMap(pois =>
			this.sitesService.lookupAddress(lookupText).pipe(
				map(addresses => [pois, addresses])
		)));
		this.lookupSearchSub = searchResults.subscribe({
			next: (data) => {
				if (Array.isArray(data)) 
				{
					let options: SearchGroup[] = [];
					if (0 < this.allBeachSites?.length) 
					{
						const matches = this.getMatches(this.allBeachSites, lookupText, "SiteName", "Identifier");						
						if(0 < matches.length)
						{
							options.push(
								{
									name: "Site",
									searchType: SearchTypes.Site,
									options : matches
								});
						}						
					}

					if (0 < this.regions.getValue().length) 
					{
						const matches = this.getMatches(this.regions.getValue(), lookupText, "RegionName", "RegionId");
						if(0 < matches.length)
						{								
							options.push(
							{
								name: "Region",
								searchType: SearchTypes.Region,
								options : matches
								// options : uniqWith(matches, (a : Region, b : Region) =>  a.RegionId === b.RegionId ||
								// 	(a.RegionType?.toString()?.toLowerCase() === "Region".toLowerCase() &&
								// 		a.RegionType === b.RegionType && a.RegionName?.toString()?.toLowerCase() === b.RegionName?.toString()?.toLowerCase()) ||
								// 	(a.RegionType?.toString()?.toLowerCase() === "Sub Region".toLowerCase() &&
								// 		a.RegionType === b.RegionType && a.RegionName?.toString()?.toLowerCase() === b.RegionName?.toString()?.toLowerCase()))
							});
						}						
					}					

					const poiTypes = <string[]>uniq((<any[]>data[0])?.filter((a : POI) => a.poiType?.toString()?.toLowerCase() === "Suburb".toLowerCase() ||
						a.poiType?.toString()?.toLowerCase() === "Location".toLowerCase()).map((a: POI) => a.poiType));
					
					const pois = (<any[]>data[0])?.map((a: any, index: number) => {						
						return {
							...a,
							id : a.poiId,
							geometry: JSON.parse(a.geometry)
						};
					}) || [];
					
					if (0 < pois.length) 
					{						 
						options = options.concat(poiTypes.map((poiType: string) => {
							return {
								name: poiType,
								options: pois.filter((poi: POI) => poi.poiType === poiType),
								searchType: SearchTypes.POI
							}
						}));
					}
					
					if (0 < (<any[]>data[1]).length) 
					{	
						options.push(
							{
								name: "Address",
								searchType: SearchTypes.Address,
								options: (<any[]>data[1])?.map((a) => ({ ...a, id : a.propId, })),
							});
					}
					
					this.searchOptionGroups.next(options);
				}
			},
			error: (error) => {
				
			},
			complete: () => {				
			}
		});	
	}

	onSearchInputChanged = (event: any): void => {		
	}

	private getMatches = (list : any[], lookupText : string,lookupProp : string, idProp : string) => {
		
		const lookupParts = lookupText.trim().split(" ");
		
		if(0 === lookupParts.length)
		{
			return [];
		}
		
		const matches = list.filter((item) => {
			let b = false;
			if(1 === lookupParts.length)
			{
				b = item?.[lookupProp]?.trim()?.toLowerCase()?.includes(lookupParts[0]?.trim()?.toLowerCase());
			}
			else
			{
				b = true;
				for(let i = 1; i < lookupParts.length; ++i)
				{
					const regExp = new RegExp(lookupParts[i - 1] + `(?: ${lookupParts[i]})`, "ig");
					b = b && regExp.test(item?.[lookupProp]?.trim());
				}
			}
			
			
			console.debug(`${item?.[lookupProp]?.trim()} : ${b}` )
			return b;
						
		}).map((a) => ({ ...a, id : a[idProp]}));

		return matches;
	}	

	onSearchKeyUp = (event: any) : void => {			

		if("Enter" === event.key)
		{
			this.executeSearch();
		}
	}

	getOptionDisplayName(option: POI | Address): string {
		let displayName = "";
		if(!option)
			return displayName;

		if (isPOI(option)) {
			displayName = (<POI>option).poiName;
		}
		else if (isAddress(option)) 
		{
			displayName = (<Address>option).address;
		}
		else if (isBeachSite(option)) 
		{
			displayName = (<BeachSite>option).SiteName;
		}
		else if (isRegion(option)) 
		{
			displayName = (<Region>option).RegionName;
		}
		else if (isSuburb(option)) 
		{
			displayName = (<Suburb>option).SuburbName;
		}
		return displayName;
	}

	getOptionId(option: POI | Address): string {
		let optionId = "";
		if(!option)
		{
			return optionId;
		}			

		if (isPOI(option)) {
			optionId = (<POI>option).poiId.toString();
		}
		else if (isAddress(option)) 
		{
			optionId = (<Address>option).propId.toString();
		}
		else if (isBeachSite(option)) 
		{
			optionId = (<BeachSite>option).Identifier;
		}
		else if (isRegion(option)) 
		{
			optionId = (<Region>option).RegionId.toString();
		}
		else if (isSuburb(option)) 
		{
			optionId = (<Suburb>option).SuburbId.toString();
		}

		return optionId;
	}

	onOptionSelected(event : any) : void {	
		this.autocomplete?.closePanel();
		this.executeSearch();			
	}

	search = (event : any) : void => {
		if(this.searchInputText)
		{			
			// this.zoomToSearchService.zoomToData = 
			// { 
			// 	area : {...this.selectedOption}, 
			// 	focusElement : this.searchInputElementRef?.nativeElement
			// };

			this.zoomToSearchService.setZoomToData( 
			{ 
				area : {...this.selectedOption}, 
				focusElement : this.searchInputElementRef?.nativeElement
			});
			
		}
	}

	onZoomToCurrentLocationClicked = () => {
		this.zoomToSearchService.setZoomToData("CURRENT_LOCATION");
	}
	
	executeSearch = () => {
		this.selectedOption = this.searchInputText;
		if(this.searchInputText)
		{	
			/*
				https://bis-jira.atlassian.net/browse/BW-380 : Searching for Regions with no active sites in the home page map search box
				Regions do not have spatial information i.e geographical boundary defined in the database
				Short-term fix/solution (as suggested by Courtney May and Joel Sellman) :
					Until the time geographical boundaries of regions/councils are defined in sales force and Beachwatch web application SQL Server database,
					display a message to the user that there are no active sites in the region and return ie. do not scroll map into view and zoom.

				Permanent fix : 
					Define boundaries for regions and councils in sales force and Beachwatch web application SQL Server database and zoom to the extent on the map.				
			*/
			if (isRegion(this.selectedOption)) 	
			{
				const region =  (<Region>this.selectedOption);
				const regionName = region.RegionName?.toString();				
				const sitesInRegion =  areaHasActiveSites(this.allBeachSites, this.regions.getValue(), region, false);				

				if(!sitesInRegion)
				{		
					const dialogData = {
						data : 
						{ 
							title : "Search",
							content : `There are no active sites in '${regionName}' region.`,
							acceptButtonText : "OK",
							showCancelButton : false							
						}};					
					this.userfeedbackModalDialog.openDialog(dialogData);					
					
					return;
				}
			}
			
			if(this.selectedOption.SiteName && this.selectedOption.Identifier){
				this.appMonitoringService.logEvent('PopupOpenFromHomePageSearch',{'site_identifier':this.selectedOption.Identifier})
			}
			this.zoomToSearchService.setZoomToData({ area : {...this.selectedOption}, 
				focusElement : this.searchInputElementRef?.nativeElement });
		}
	}
}
